/**
 * BCE Solutions Alternative Module
 * 
 * Alternative version with sidebar layout - image area beside navigation instead of behind
 * Only supports vertical sidebar layout (no horizontal)
 * 
 * Key differences from original:
 * - Uses bce_solutions identifier instead of bce_solutions
 * - Only vertical layout support
 * - Sidebar with dark gray background (instead of semi-transparent)
 * - Image area positioned beside sidebar (not behind)
 * - Background image applied to teaser-image-area instead of teaser-wrapper
 * 
 * @class BceSolutions
 */
class BceSolutions {
    constructor() {
        this.instances = new WeakMap();
        this.autoInit = false;
    }

    /**
     * Initialize all solutions elements found in the DOM
     * Automatically called by BCE system on page load
     */
    init() {
        const solutionsContainers = document.querySelectorAll('.bce_solutions');
        solutionsContainers.forEach(container => {
            if (!this.instances.has(container)) {
                this.create(container);
            }
        });
    }

    /**
     * Create a solutions instance for a container
     * 
     * @param {HTMLElement} container - The container element with bce_solutions class
     * @returns {Object|null} Solutions instance or null
     */
    create(container) {
        if (this.instances.has(container)) {
            return this.instances.get(container);
        }

        // Check if already transformed structure exists (prevents double initialization)
        const existingTeaserContainer = container.querySelector('.product-teaser-container');
        if (existingTeaserContainer) {
            const defaultBackgroundUrl = this.extractDefaultBackground(existingTeaserContainer) || '';
            const solutionsInstance = this.initializeSolutions(existingTeaserContainer, defaultBackgroundUrl);
            this.instances.set(container, solutionsInstance);
            return solutionsInstance;
        }

        // Find the ratio container within the solutions container (original structure)
        const ratioContainer = container.querySelector('.ratio.ratio-16x9.overflow-hidden');
        if (!ratioContainer) {
            console.warn('Solutions: No ratio container or product-teaser-container found');
            return null;
        }

        // Clean up container classes and ensure correct layout class is set
        // Force remove any legacy classes and set only the unified class
        container.className = '';
        container.classList.add('bce_solutions');
        
        // Remove any legacy classes that might be left over
        container.classList.remove('bce_solutions-vertical', 'bce_solutions-horizontal', 'solutions-vertical', 'solutions-horizontal');

        // Extract default background image
        const defaultBackgroundUrl = this.extractDefaultBackground(ratioContainer);

        // Extract navigation items and content
        const { titles, descriptions, backgroundUrls } = this.extractContent(ratioContainer, defaultBackgroundUrl);

        if (!titles.length || !descriptions.length) {
            console.warn('Solutions: No content found to process');
            return null;
        }

        // Build new structure with alternative layout
        this.buildSolutionsStructure(ratioContainer, titles, descriptions, backgroundUrls, defaultBackgroundUrl);

        // Initialize functionality
        const solutionsInstance = this.initializeSolutions(ratioContainer, defaultBackgroundUrl);

        // Store instance
        this.instances.set(container, solutionsInstance);

        return solutionsInstance;
    }

    /**
     * Extract default background image from navigation-teaser-background
     * 
     * @param {HTMLElement} container - The ratio container
     * @returns {string} Default background URL
     */
    extractDefaultBackground(container) {
        // First try to find navigation-teaser-background (original structure)
        const backgroundPicture = container.querySelector('.navigation-teaser-background picture');
        if (backgroundPicture) {
            const viewportWidth = window.innerWidth;
            const url = this.getImageUrlFromPicture(backgroundPicture, viewportWidth);
            return url;
        }
        
        // For already transformed containers, try to get from first nav item
        const firstNavItem = container.querySelector('.teaser-nav-item');
        if (firstNavItem && firstNavItem.dataset.backgroundUrl) {
            return firstNavItem.dataset.backgroundUrl;
        }
        
        return '';
    }

    /**
     * Extract content from existing nav items
     * 
     * @param {HTMLElement} container - The ratio container
     * @param {string} defaultBackgroundUrl - Fallback background URL
     * @returns {Object} Extracted content data
     */
    extractContent(container, defaultBackgroundUrl) {
        let navItems = container.querySelectorAll('.nav-item');
        if (!navItems.length) {
            navItems = container.querySelectorAll('.navigation-teaser-container .nav-item');
        }

        const titles = [];
        const descriptions = [];
        const backgroundUrls = [];

        navItems.forEach((item, index) => {
            const titleElement = item.querySelector('.title p');
            const descriptionElement = item.querySelector('.navigation-teaser-description');
            const pictureElement = item.querySelector('picture');

            if (titleElement && descriptionElement) {
                const title = titleElement.textContent;
                const description = descriptionElement.innerHTML;
                
                titles.push({ text: title, index: index });
                descriptions.push({ content: description, index: index });

                // Extract background image URL
                if (pictureElement) {
                    const imageUrl = this.getImageUrlFromPicture(pictureElement, window.innerWidth);
                    backgroundUrls.push({ url: imageUrl, index: index });
                } else {
                    backgroundUrls.push({ url: defaultBackgroundUrl, index: index });
                }
            }
        });

        return { titles, descriptions, backgroundUrls };
    }

    /**
     * Build the new solutions DOM structure with sidebar layout
     * 
     * @param {HTMLElement} container - The ratio container
     * @param {Array} titles - Title data
     * @param {Array} descriptions - Description data
     * @param {Array} backgroundUrls - Background URL data
     * @param {string} defaultBackgroundUrl - Default background URL
     */
    buildSolutionsStructure(container, titles, descriptions, backgroundUrls, defaultBackgroundUrl) {
        // Clear existing content
        container.innerHTML = '';
        container.className = 'product-teaser-container';

        // Create wrapper structure for alternative layout
        const wrapperDiv = document.createElement('div');
        wrapperDiv.className = 'teaser-wrapper';
        container.appendChild(wrapperDiv);

        // Create sidebar navigation (left)
        const navColumn = document.createElement('div');
        navColumn.className = 'teaser-nav';
        wrapperDiv.appendChild(navColumn);

        // Create image area (right side) - this will contain the background image
        const imageArea = document.createElement('div');
        imageArea.className = 'teaser-image-area';
        wrapperDiv.appendChild(imageArea);

        // Create content column inside image area
        const contentColumn = document.createElement('div');
        contentColumn.className = 'teaser-content';
        imageArea.appendChild(contentColumn);

        // Create navigation items
        titles.forEach(({ text, index }) => {
            const navItem = document.createElement('div');
            navItem.className = 'teaser-nav-item';
            navItem.textContent = text;
            navItem.dataset.content = `description-${index}`;
            navItem.dataset.backgroundUrl = backgroundUrls[index]?.url || defaultBackgroundUrl;
            navItem.dataset.title = text;
            navColumn.appendChild(navItem);
        });

        // Create description sections
        descriptions.forEach(({ content, index }) => {
            const descriptionDiv = document.createElement('div');
            descriptionDiv.className = 'description';
            descriptionDiv.id = `description-${index}`;

            const descriptionTitle = document.createElement('h3');
            descriptionTitle.className = 'description-title';
            descriptionTitle.textContent = titles[index].text;
            descriptionDiv.appendChild(descriptionTitle);

            const descriptionContent = document.createElement('div');
            descriptionContent.innerHTML = content;
            descriptionDiv.appendChild(descriptionContent);

            contentColumn.appendChild(descriptionDiv);
        });
    }

    /**
     * Initialize solutions functionality with events and background switching
     * 
     * @param {HTMLElement} container - The product teaser container
     * @param {string} defaultBackgroundUrl - Default background URL
     * @returns {Object} Solutions instance
     */
    initializeSolutions(container, defaultBackgroundUrl) {
        const navItems = container.querySelectorAll('.teaser-nav-item');
        const contentColumn = container.querySelector('.teaser-content');
        const imageArea = container.querySelector('.teaser-image-area');
        let activeDescription = null;

        // Create mobile accordion version
        console.log('Creating mobile accordion for container:', container.id);
        try {
            this.createMobileAccordion(container);
            console.log('Mobile accordion creation completed');
        } catch (error) {
            console.error('Error creating mobile accordion:', error);
        }
        let activeNavItem = null;

        // Determine interaction method based on device (992px breakpoint)
        const isMobile = window.innerWidth <= 992;
        const useClickEvents = isMobile;

        // Set background image function - apply to teaser-image-area instead of teaser-wrapper.
        // Resolve to an absolute URL against the page origin: the CSS that consumes
        // var(--bg-image) is served from a different origin (Cloudflare edge), so a
        // relative URL would otherwise be resolved against that stylesheet -> 404.
        const setBackgroundImage = (url) => {
            if (url && imageArea) {
                let abs = url;
                try { abs = new URL(url, document.baseURI).href; } catch (e) {}
                imageArea.style.setProperty('--bg-image', `url('${abs}')`);
            }
        };

        // Set default background image initially. If there is none, the CSS
        // neutral fallback (background-color) takes over — no placeholder image.
        if (defaultBackgroundUrl) {
            setBackgroundImage(defaultBackgroundUrl);
        }

        // Add event listeners to navigation items
        navItems.forEach(item => {
            const eventType = useClickEvents ? 'click' : 'mouseenter';

            item.addEventListener(eventType, (event) => {
                event.stopPropagation();

                // Update active nav item
                if (activeNavItem) {
                    activeNavItem.classList.remove('active-nav-item');
                }
                item.classList.add('active-nav-item');
                activeNavItem = item;

                // Switch background image
                setBackgroundImage(item.dataset.backgroundUrl);

                // Hide all descriptions first
                contentColumn.querySelectorAll('.description').forEach(desc => {
                    desc.classList.remove('active');
                });

                // Show target description
                const targetDescription = contentColumn.querySelector(`#${item.dataset.content}`);
                if (targetDescription) {
                    targetDescription.classList.add('active');
                    activeDescription = targetDescription;
                    // Show content area
                    contentColumn.classList.add('has-active-content');
                }
            });
        });

        // Mouse leave handling for desktop
        if (!isMobile) {
            container.addEventListener('mouseleave', () => {
                // Hide all descriptions
                contentColumn.querySelectorAll('.description').forEach(desc => {
                    desc.classList.remove('active');
                });
                // Remove active state from nav items
                if (activeNavItem) {
                    activeNavItem.classList.remove('active-nav-item');
                }
                // Hide content area
                contentColumn.classList.remove('has-active-content');
                // Reset states
                activeDescription = null;
                activeNavItem = null;
                // Return to default background
                setBackgroundImage(defaultBackgroundUrl);
            });
        }

        return {
            container,
            navItems,
            contentColumn,
            imageArea,
            setBackgroundImage,
            destroy: () => {
                // Clean up event listeners if needed
                navItems.forEach(item => {
                    item.replaceWith(item.cloneNode(true));
                });
            }
        };
    }

    /**
     * Extract image URL from picture element with WebP support
     * 
     * @param {HTMLElement} pictureElement - The picture element
     * @param {number} viewportWidth - Current viewport width
     * @returns {string} Selected image URL
     */
    getImageUrlFromPicture(pictureElement, viewportWidth) {
        // Prefer the source the browser already resolved for the visible <picture>.
        // Reusing it is an instant cache hit (no re-download) and picks the correctly
        // sized variant instead of always grabbing the largest one via the fallback below.
        const imgEl = pictureElement.querySelector('img');
        if (imgEl && imgEl.currentSrc) {
            return imgEl.currentSrc;
        }

        const sources = pictureElement.querySelectorAll('source');
        let selectedUrl = imgEl?.src || '';

        const supportsWebP = this.supportsWebPFormat();

        for (const source of sources) {
            const media = source.getAttribute('media');
            const type = source.getAttribute('type');
            const srcSet = source.getAttribute('srcset');

            if (!srcSet) continue;

            // Check media query
            let mediaMatch = true;
            if (media) {
                const minWidthMatch = media.match(/min-width:\s*(\d+)px/);
                if (minWidthMatch) {
                    const minWidth = parseInt(minWidthMatch[1], 10);
                    mediaMatch = viewportWidth >= minWidth;
                }
            }

            // Prefer WebP if supported
            if (mediaMatch) {
                if (supportsWebP && type === 'image/webp') {
                    selectedUrl = this.getLargestImageFromSrcSet(srcSet);
                    break;
                } else if (!type || type !== 'image/webp') {
                    selectedUrl = this.getLargestImageFromSrcSet(srcSet);
                }
            }
        }

        return selectedUrl;
    }

    /**
     * Extract largest image URL from srcset
     * 
     * @param {string} srcSet - The srcset attribute value
     * @returns {string} Largest image URL
     */
    getLargestImageFromSrcSet(srcSet) {
        const srcSetItems = srcSet.split(',').map(item => item.trim().split(' '));
        let largestUrl = srcSetItems[0][0];
        let largestWidth = 0;

        srcSetItems.forEach(([url, width]) => {
            const w = parseInt(width, 10) || 0;
            if (w > largestWidth) {
                largestWidth = w;
                largestUrl = url;
            }
        });

        return largestUrl;
    }

    /**
     * Check if browser supports WebP format
     * 
     * @returns {boolean} WebP support status
     */
    supportsWebPFormat() {
        const elem = document.createElement('canvas');
        if (!!(elem.getContext && elem.getContext('2d'))) {
            return elem.toDataURL('image/webp').indexOf('data:image/webp') === 0;
        }
        return false;
    }

    /**
     * Destroy a solutions instance
     * 
     * @param {HTMLElement} container - The solutions container
     */
    destroy(container) {
        const instance = this.instances.get(container);
        if (instance && instance.destroy) {
            instance.destroy();
        }
        this.instances.delete(container);
    }

    /**
     * Get instance for external access
     * 
     * @param {HTMLElement} container - The solutions container
     * @returns {Object|null} Solutions instance or null
     */
    getInstance(container) {
        return this.instances.get(container);
    }

    /**
     * Create mobile Bootstrap accordion version from existing data
     */
    createMobileAccordion(container) {
        // Extract data from existing desktop structure
        const navItems = container.querySelectorAll('.teaser-nav-item');
        const descriptions = container.querySelectorAll('.teaser-content .description');
        const containerId = container.id || 'solutions';
        
        // Create mobile accordion container
        const mobileAccordion = document.createElement('div');
        mobileAccordion.className = 'mobile-accordion-version';
        
        // Add header (copy from original)
        const header = container.querySelector('header');
        if (header) {
            const mobileHeader = header.cloneNode(true);
            mobileAccordion.appendChild(mobileHeader);
        }
        
        // Add lead text (copy from original)
        const textCenter = container.querySelector('.text-center');
        if (textCenter) {
            const mobileTextCenter = textCenter.cloneNode(true);
            mobileAccordion.appendChild(mobileTextCenter);
        }
        
        // Create Bootstrap accordion container
        const accordionContainer = document.createElement('div');
        accordionContainer.className = 'accordion';
        accordionContainer.id = `accordion-${containerId}`;
        
        // Create accordion items from existing data
        navItems.forEach((navItem, index) => {
            const title = navItem.textContent.trim();
            const backgroundUrl = navItem.dataset.backgroundUrl;
            const description = descriptions[index];
            
            if (description) {
                const accordionItemId = `accordion-${containerId}-${index}`;
                
                // Create Bootstrap accordion item
                const accordionItem = document.createElement('div');
                accordionItem.className = 'accordion-item';
                
                // Accordion header with Bootstrap classes
                const accordionHeader = document.createElement('h4');
                accordionHeader.className = 'accordion-header';
                
                const accordionButton = document.createElement('button');
                accordionButton.className = 'accordion-button collapsed';
                accordionButton.type = 'button';
                accordionButton.setAttribute('data-bs-toggle', 'collapse');
                accordionButton.setAttribute('data-bs-target', `#${accordionItemId}`);
                accordionButton.setAttribute('aria-expanded', 'false');
                accordionButton.setAttribute('aria-controls', accordionItemId);
                accordionButton.setAttribute('data-bs-parent', `#accordion-${containerId}`);
                accordionButton.innerHTML = `${title}<span class="accordion-icon"></span>`;
                
                accordionHeader.appendChild(accordionButton);
                
                // Accordion collapse div
                const accordionCollapse = document.createElement('div');
                accordionCollapse.id = accordionItemId;
                accordionCollapse.className = 'accordion-collapse collapse';
                accordionCollapse.setAttribute('aria-labelledby', accordionItemId);
                accordionCollapse.setAttribute('data-bs-parent', `#accordion-${containerId}`);
                
                // Accordion body
                const accordionBody = document.createElement('div');
                accordionBody.className = 'accordion-body';
                
                // Add image div if available (16:9 aspect ratio)
                if (backgroundUrl) {
                    const imageDiv = document.createElement('div');
                    imageDiv.className = 'accordion-image';
                    // Absolute URL against the page origin (see setBackgroundImage note).
                    let absBg = backgroundUrl;
                    try { absBg = new URL(backgroundUrl, document.baseURI).href; } catch (e) {}
                    imageDiv.style.backgroundImage = `url('${absBg}')`;
                    accordionBody.appendChild(imageDiv);
                }
                
                // Content container for text
                const contentContainer = document.createElement('div');
                contentContainer.className = 'accordion-text-content';
                
                // Clean description content (remove mobile-specific elements)
                const cleanContent = description.cloneNode(true);
                const mobileElements = cleanContent.querySelectorAll('.d-flex.d-lg-none, .block.d-lg-none');
                mobileElements.forEach(el => el.remove());
                
                contentContainer.innerHTML = cleanContent.innerHTML;
                accordionBody.appendChild(contentContainer);
                
                accordionCollapse.appendChild(accordionBody);
                accordionItem.appendChild(accordionHeader);
                accordionItem.appendChild(accordionCollapse);
                accordionContainer.appendChild(accordionItem);
            }
        });
        
        mobileAccordion.appendChild(accordionContainer);
        
        // Insert mobile accordion after the desktop version (as sibling, not child)
        const parentContainer = container.parentNode;
        parentContainer.insertBefore(mobileAccordion, container.nextSibling);
        
        console.log('BCE Solutions: Bootstrap mobile accordion created');
    }
}

export default new BceSolutions();